import { PageError } from '@/components/PageError'
import { PageLoader } from '@/components/PageLoader'
import { useAppliedTheme } from '@/components/ThemeProvider/hooks'
import { useReportDetails } from '@/hooks/queries/useReports'
import { isValidUrl } from '@/lib/utils/strings'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

/**
 * Filter values a report embed can be opened with, e.g. when one report links to
 * another for a specific record. Each name must match a template tag on the
 * Metabase question and be marked as an editable embedding parameter there;
 * Metabase reads editable parameters from the embed URL's query string.
 */
export const reportEmbedFiltersSchema = z.object({
  order_number: z.string().optional(),
  po_number: z.string().optional(),
  sap_erp: z.string().optional(),
  material: z.string().optional(),
})

export type ReportEmbedFilters = z.infer<typeof reportEmbedFiltersSchema>

/** Adds the filter values to the embed URL's query string, ahead of its fragment. */
function withFilters(embedURL: string, filters: ReportEmbedFilters) {
  const search = new URLSearchParams(
    Object.entries(filters).filter((entry): entry is [string, string] => Boolean(entry[1])),
  ).toString()

  if (!search) return embedURL

  const [base, fragment] = embedURL.split('#')
  return `${base}?${search}${fragment ? `#${fragment}` : ''}`
}

export function ReportDetailsEmbed(props: { reportId: string; filters?: ReportEmbedFilters }) {
  const { t } = useTranslation('reports')

  const { isPending, error, data: report } = useReportDetails(props.reportId)
  const { isDarkTheme } = useAppliedTheme()

  if (isPending) return <PageLoader />

  if (error || !isValidUrl(report.embedURL))
    return <PageError message={error ? error.message : t('reportDetailsEmbed.invalidEmbedUrl')} />

  const embedURL =
    withFilters(report.embedURL, props.filters ?? {}) + (isDarkTheme ? '&theme=night' : '')

  return (
    <div className="h-[calc(100vh-110px)] w-full p-4">
      <iframe className="h-full w-full overflow-clip rounded-md border" src={embedURL} />
    </div>
  )
}
